/**
 * SanPlumb — Motor de cálculo Hunter (IS.010 §2.3 + Anexo 3)
 *
 * Funciones puras (sin side effects) que convierten Unidades Hunter de Gasto
 * (UG) acumuladas en:
 *   1. Caudal probable Q (L/s) — vía interpolación en la tabla del Anexo 3
 *   2. Diámetro nominal mínimo necesario
 *   3. Velocidad resultante en ese diámetro
 *   4. Validación contra velocidades máximas §2.3(f)
 *
 * Origen de migración: legacy `sanplumb-mvp__1_.html` líneas 487-511.
 * Cambios respecto al MVP:
 *   - Funciones extraídas a módulo dedicado (en el MVP estaban en el scope global)
 *   - Tipado estricto: el caller obtiene Diameter en lugar de string libre
 *   - Nueva función `selectDiameterByVelocity()` que combina diámetro mínimo
 *     por caudal + escalamiento si velocidad excede máxima permitida
 */

import { HUNTER_TABLE, VELOCIDADES_MAX, DIAM_TO_MM } from '../constants/is010';
import type { Diameter } from '../types';

// ──────────────────────────────────────────────────────────────────
// interpolateHunter — UG → Q (L/s)
// ──────────────────────────────────────────────────────────────────

/**
 * Interpola el caudal probable Q (L/s) a partir de las Unidades de Gasto
 * acumuladas, usando la tabla Hunter del Anexo 3.
 *
 * @param ug          Unidades de Gasto acumuladas (≥ 0).
 * @param useValvula  Si true, usa la columna de descarga por válvula
 *                    semiautomática. Por defecto false (descarga por tanque).
 *                    Cuando la columna de válvula es null para una fila
 *                    (UG bajas), cae automáticamente a la columna de tanque.
 * @returns           Caudal probable en L/s, redondeado a 2 decimales.
 */
export function interpolateHunter(ug: number, useValvula = false): number {
  if (ug <= 0) return 0;

  const col = useValvula ? 2 : 1;

  // Búsqueda lineal entre filas adyacentes
  for (let i = 0; i < HUNTER_TABLE.length - 1; i++) {
    const rowA = HUNTER_TABLE[i];
    const rowB = HUNTER_TABLE[i + 1];

    if (ug >= rowA[0] && ug <= rowB[0]) {
      const t = (ug - rowA[0]) / (rowB[0] - rowA[0]);
      // Si la columna de válvula es null, usa tanque
      const a = (rowA[col] as number | null) ?? rowA[1];
      const b = (rowB[col] as number | null) ?? rowB[1];
      return +(a + (b - a) * t).toFixed(2);
    }
  }

  // UG fuera del rango superior de la tabla → cap al máximo
  const last = HUNTER_TABLE[HUNTER_TABLE.length - 1];
  if (ug > last[0]) {
    return (last[col] as number | null) ?? last[1];
  }

  // UG menor al primer punto de la tabla (no debería ocurrir con ug > 0
  // y la tabla empezando en 3, pero defensivo)
  const first = HUNTER_TABLE[0];
  return (first[col] as number | null) ?? first[1];
}

// ──────────────────────────────────────────────────────────────────
// getDiameter — Q → diámetro nominal mínimo
// ──────────────────────────────────────────────────────────────────

/** Resultado del selector de diámetro por caudal. */
export interface DiameterResult {
  /** Diámetro nominal en formato string ('1/2"', '3/4"', '1"', '1¼"'). */
  d: Diameter;
  /** Equivalente métrico en mm (15, 20, 25, 32). */
  mm: number;
}

/**
 * Devuelve el diámetro nominal mínimo necesario para un caudal dado.
 * Los rangos provienen del MVP y son consistentes con la práctica peruana
 * para tuberías PVC-P / CPVC en residencial.
 *
 * Umbrales (L/s):
 *   ≤ 0.25 → ½"
 *   ≤ 0.48 → ¾"
 *   ≤ 0.72 → 1"
 *    > 0.72 → 1¼"
 */
export function getDiameter(q_ls: number): DiameterResult {
  if (q_ls <= 0.25) return { d: '1/2"', mm: 15 };
  if (q_ls <= 0.48) return { d: '3/4"', mm: 20 };
  if (q_ls <= 0.72) return { d: '1"', mm: 25 };
  return { d: '1¼"', mm: 32 };
}

// ──────────────────────────────────────────────────────────────────
// getVelocity — Q + diámetro → velocidad (m/s)
// ──────────────────────────────────────────────────────────────────

/**
 * Calcula la velocidad de flujo (m/s) para un caudal y diámetro dados.
 * Usa el área transversal del tubo (π · r²) donde r = mm/2000 (mm → m).
 *
 * @param q_ls  Caudal en L/s.
 * @param mm    Diámetro nominal en mm.
 * @returns     Velocidad en m/s, redondeada a 2 decimales.
 */
export function getVelocity(q_ls: number, mm: number): number {
  const area_m2 = Math.PI * Math.pow(mm / 2000, 2); // πr² donde r en m
  const q_m3s = q_ls / 1000; // L/s → m³/s
  return +(q_m3s / area_m2).toFixed(2);
}

// ──────────────────────────────────────────────────────────────────
// selectDiameterByVelocity — diámetro mínimo CON validación de velocidad
// ──────────────────────────────────────────────────────────────────

/** Resultado completo del cálculo: diámetro elegido + velocidad + conformidad. */
export interface SelectionResult extends DiameterResult {
  /** Velocidad resultante (m/s) en el diámetro elegido. */
  v: number;
  /** Velocidad máxima permitida (m/s) para ese diámetro — §2.3(f). */
  vMax: number;
  /** True si v ≤ vMax. */
  ok: boolean;
  /** True si el diámetro tuvo que ser escalado por exceso de velocidad. */
  scaled: boolean;
}

/** Orden ascendente de diámetros disponibles (para escalamiento). */
const DIAMETER_ORDER: readonly Diameter[] = ['1/2"', '3/4"', '1"', '1¼"'];

/**
 * Selecciona el diámetro nominal mínimo que cumple AMBAS condiciones:
 *   1) Soporta el caudal según los umbrales de `getDiameter()`.
 *   2) La velocidad resultante no excede la máxima del Anexo §2.3(f).
 *
 * Si el diámetro nominal por caudal hace que se exceda la velocidad máxima,
 * escala al siguiente diámetro disponible hasta cumplir o agotarse la tabla.
 *
 * @param q_ls  Caudal en L/s.
 * @returns     Diámetro recomendado con validación de velocidad.
 */
export function selectDiameterByVelocity(q_ls: number): SelectionResult {
  // Punto de partida: diámetro mínimo por caudal
  const initial = getDiameter(q_ls);
  let idx = DIAMETER_ORDER.indexOf(initial.d);
  let scaled = false;

  while (idx < DIAMETER_ORDER.length) {
    const d = DIAMETER_ORDER[idx];
    const mm = DIAM_TO_MM[d];
    const v = getVelocity(q_ls, mm);
    const vMax = VELOCIDADES_MAX[mm];

    if (v <= vMax) {
      return { d, mm, v, vMax, ok: true, scaled };
    }

    // Excede velocidad — intentar siguiente diámetro
    idx++;
    scaled = true;
  }

  // Caso extremo: ningún diámetro de la tabla cumple — devolver el máximo
  // con ok=false para que el caller decida (mostrar advertencia, etc.)
  const d = DIAMETER_ORDER[DIAMETER_ORDER.length - 1];
  const mm = DIAM_TO_MM[d];
  return {
    d,
    mm,
    v: getVelocity(q_ls, mm),
    vMax: VELOCIDADES_MAX[mm],
    ok: false,
    scaled: true,
  };
}
